import AIButton from '@cobliteam/ai/src/ui/AIButton/AIButton';
import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Location } from '@cobliteam/common-types';
import { useRiskRecognitionList } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions';
import { useArchiveRiskRecognition, useUnarchiveRiskRecognition } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useArchiveRiskRecognition';
import { useTranslation } from '@cobliteam/i18n';
import Badge from '@cobliteam/ui/components/Badge';
import Button from '@cobliteam/ui/components/Button';
import Divider from '@cobliteam/ui/components/Divider';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Tabs, { TabsList, TabsTrigger } from '@cobliteam/ui/components/Tabs';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useNotificationDrawerStore from '../../core/store/useNotificationDrawerStore';
import { RISK_DRAWER_PARAM } from '../../types/risk-recognition/parse';
import RiskNotificationRow from '../RiskNotificationRow/RiskNotificationRow';

/**
 * Sino do feed in-app do header. Fonte = `GET /v1/risk-recognitions/highlights` (RiskRecognitionsRequester),
 * com abas Entrada/Arquivadas. Sem Knock. Item → abre o drawer; arquivar/desarquivar inline (otimista);
 * "Ver todas" leva à página dedicada com scroll infinito.
 */
const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';

const FeedSkeleton = () => (
  <div className='flex gap-3 px-6 py-4'>
    <Skeleton className='mt-0.5 size-9 shrink-0 rounded-full' />
    <div className='min-w-0 flex-1 space-y-3'>
      <div className='space-y-2'>
        <Skeleton className='h-4 w-3/5' />
        <Skeleton className='h-3 w-full' />
      </div>
      <Skeleton className='h-3 w-24' />
    </div>
  </div>
);

const NotificationFeedButton = () => {
  const { t } = useTranslation('notifications');
  const history = useHistory();
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);
  const { mutate: archive } = useArchiveRiskRecognition();
  const { mutate: unarchive } = useUnarchiveRiskRecognition();
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const isArchivedView = tab === 'archived';

  // Badge sempre reflete a entrada (ativas), independente da aba aberta. Quando a aba é "active" as duas
  // queries compartilham a mesma key (sem fetch extra).
  const { items: activeItems } = useRiskRecognitionList({ archived: false });
  const { items, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } = useRiskRecognitionList({ archived: isArchivedView });
  const hasRisk = activeItems.length > 0;
  const hasItems = items.length > 0;
  const isSettled = !(isLoading || isError);

  // Scroll infinito (keyset por `before`): um sentinel após a lista dispara `fetchNextPage` ao entrar na
  // viewport do container de scroll do popover. Usamos CALLBACK REFS (não useEffect) porque o Radix monta o
  // PopoverContent — e portanto os nós root/sentinel — num commit POSTERIOR ao que vira `open=true`; um efeito
  // rodaria com os refs ainda nulos e o observer nunca anexaria. Os callbacks disparam quando os nós de fato
  // montam (sentinel/filho antes de root/pai), então `setupObserver` só cria o observer quando ambos existem.
  const rootNodeRef = useRef<HTMLDivElement | null>(null);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Valores frescos pro callback do observer, sem recriá-lo a cada render.
  const pagingRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  pagingRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage };

  const setupObserver = useCallback(() => {
    observerRef.current?.disconnect();
    if (!(rootNodeRef.current && sentinelNodeRef.current)) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const paging = pagingRef.current;
        if (entry?.isIntersecting && paging.hasNextPage && !paging.isFetchingNextPage) void paging.fetchNextPage();
      },
      { root: rootNodeRef.current, rootMargin: '200px' }
    );
    observerRef.current.observe(sentinelNodeRef.current);
  }, []);

  const setScrollNode = useCallback(
    (node: HTMLDivElement | null) => {
      rootNodeRef.current = node;
      setupObserver();
    },
    [setupObserver]
  );

  const setSentinelNode = useCallback(
    (node: HTMLDivElement | null) => {
      sentinelNodeRef.current = node;
      setupObserver();
    },
    [setupObserver]
  );

  // Re-arma o observer quando a lista cresce: o IntersectionObserver só dispara na MUDANÇA de interseção; se o
  // sentinel continua visível após carregar uma página (container curto), não re-dispararia e a paginação
  // travaria. Reconectar força uma nova avaliação inicial — encadeia até o sentinel sair da view ou acabar.
  // biome-ignore lint/correctness/useExhaustiveDependencies: items.length/hasNextPage são gatilhos de re-arme, não lidos dentro.
  useEffect(() => {
    setupObserver();
  }, [items.length, hasNextPage, setupObserver]);

  const handleOpen = (highlight: RiskRecognitionHighlight) => {
    Analytics.track('AI - Risk Recognition Feed - Action Clicked', { ruleName: highlight.metadata?.rule_name, hasAction: false, surface: 'bell' });
    setIsOpen(false);
    openDrawer(RISK_RECOGNITION_TYPE_KEY, highlight);
    const params = new URLSearchParams(history.location.search);
    params.set(RISK_DRAWER_PARAM, highlight.id);
    history.push({ search: `?${params.toString()}` });
  };

  const handleArchiveToggle = (highlight: RiskRecognitionHighlight) => {
    Analytics.track(isArchivedView ? 'AI - Risk Recognition Feed - Item Unarchived' : 'AI - Risk Recognition Feed - Item Archived', {
      ruleName: highlight.metadata?.rule_name,
      surface: 'bell',
    });
    if (isArchivedView) unarchive(highlight.id);
    else archive(highlight.id);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) Analytics.track('AI - Risk Recognition Feed - Opened', { hasRisk });
    setIsOpen(open);
  };

  const handleSeeAll = () => {
    Analytics.track('AI - Risk Recognition Feed - See All Clicked');
    setIsOpen(false);
    history.push(Location.RISK_NOTIFICATIONS);
  };

  const handleTabChange = (value: string) => {
    Analytics.track('AI - Risk Recognition Feed - Tab Changed', { selectedTab: value, surface: 'bell' });
    setTab(value as 'active' | 'archived');
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip content={t('feed.tooltip')} position='right' asChild>
        <PopoverTrigger asChild>
          <div className='relative'>
            <Badge variant='dot' hide={!hasRisk} position='top-right' color='danger' size='xs'>
              <AIButton
                variant='ai-outline'
                data-testid='app-header-notification-feed-button'
                size='sm'
                icon={<Icon name='message-square-warning' size='sm' />}
                aria-label={t('feed.tooltip')}
              />
            </Badge>
          </div>
        </PopoverTrigger>
      </Tooltip>

      <PopoverContent
        align='end'
        side='right'
        showArrow={false}
        onOpenAutoFocus={(event) => event.preventDefault()}
        // z abaixo do modal (z-modal=10000): o modal de footage é acionado de dentro do sino e precisa ficar
        // por cima (o PopoverContent usa z-tooltip=11000 por padrão). `!` pra vencer o z-tooltip no merge.
        className='!z-[9990] flex w-[440px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl border-gray-6 p-0 shadow-xl'
      >
        <div className='flex items-center justify-between gap-3 border-gray-5 border-b py-3 pr-3 pl-6'>
          <Typography as='h2' variant='heading-lg'>
            {t('feed.title')}
          </Typography>
          <Button type='button' size='sm' variant='ghost' color='default' icon={<Icon name='x' />} aria-label={t('feed.actions.close')} onClick={() => setIsOpen(false)} />
        </div>

        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList variant='line' className='px-4'>
            <TabsTrigger value='active' size='md'>
              {t('feed.tabs.all')}
            </TabsTrigger>
            <TabsTrigger value='archived' size='md'>
              {t('feed.tabs.archived')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div ref={setScrollNode} className='max-h-[min(560px,calc(100vh-8rem))] overflow-y-auto'>
          {isLoading && (
            <div role='status' aria-label={t('feed.states.loading')}>
              <FeedSkeleton />
            </div>
          )}

          {isError && !isLoading && (
            <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
              <Icon name='triangle-alert' className='text-danger' aria-hidden />
              <Typography variant='text-sm-medium'>{t('feed.states.error_title')}</Typography>
              <Typography variant='caption-xs' color='secondary' className='text-pretty'>
                {t('feed.states.error_description')}
              </Typography>
            </div>
          )}

          {isSettled && !hasItems && (
            <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
              <div className='flex size-10 items-center justify-center rounded-full bg-gray-3 text-gray-10'>
                <Icon name='bell-off' aria-hidden />
              </div>
              <Typography variant='text-sm-medium'>{t('feed.states.empty_title')}</Typography>
              <Typography variant='caption-xs' color='secondary' className='text-pretty'>
                {t('feed.states.empty_description')}
              </Typography>
            </div>
          )}

          {isSettled &&
            hasItems &&
            items.map((highlight, index) => (
              <Fragment key={highlight.id}>
                {index > 0 && <Divider />}
                <RiskNotificationRow highlight={highlight} isArchivedView={isArchivedView} onOpen={handleOpen} onArchiveToggle={handleArchiveToggle} />
              </Fragment>
            ))}

          {isSettled && hasItems && isFetchingNextPage && (
            <div role='status' aria-label={t('feed.states.loading')}>
              <Divider />
              <FeedSkeleton />
            </div>
          )}

          {/* Sentinel do scroll infinito: ao entrar na viewport, carrega a próxima página keyset. */}
          {isSettled && hasItems && <div ref={setSentinelNode} aria-hidden className='h-px w-full' />}
        </div>

        {/* {isSettled && (
          <div className='border-gray-5 border-t p-2'>
            <Button type='button' size='sm' variant='ghost' color='primary' className='w-full' onClick={handleSeeAll}>
              {t('feed.actions.see_all')}
            </Button>
          </div>
        )} */}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationFeedButton;
